import { ColumnFiltersState, RowSelectionState } from "@tanstack/react-table";
import { Dispatch, SetStateAction, useCallback, useMemo, useRef } from "react";
import type { RebalanceWithMetrics } from "../../../api/src/rebalances/rebalances.service";
import { useCustomFieldsFilter } from "../components/Table/CustomFieldsFilter";
import {
  type CustomFieldFilter,
  hiddenRowIds,
  matchesCustomFieldFilter,
  usableDefinitions,
} from "../components/Table/customFieldFilter";
import type {
  SetTableSettings,
  TableSettings,
} from "../components/Table/tableSettings";
import { useQueryHouseholdCustomFields } from "../components/Table/useQueryHouseholdCustomFields";
import { useQueryCrmUsers } from "../firm/lib";
import { RebalanceRow, mapRebalancesToRebalanceRows } from "./lib";

/** The id of a row of a rebalance list: `rebalanceId-level-id`. */
export const getRebalanceRowId = (row: RebalanceRow) =>
  `${row.rebalanceId}-${row.level}-${row.id}`;

/** The account rows of a rebalance row. */
export const getRebalanceSubRows = (row: RebalanceRow) => row.accounts ?? [];

/**
 * `filters` without a Service Tier filter.
 * Approved and Executed have no Service Tier column for a user not connected
 * to Slant; a saved filter would otherwise still hide rows with no control
 * left to show or clear it.
 */
export const withoutServiceTierFilter = (filters: ColumnFiltersState) =>
  filters.filter((filter) => filter.id !== "serviceTier");

/**
 * The rows of a rebalance list (Suggested, Approved, Executed) with the
 * filters every list shares: each household row's assigned user, for the
 * "Assigned To" column, and the custom field filter (spec
 * `doc-custom-field-filtering-01a11cc6e668eb59`, R14, R30 to R34, R37), saved
 * in the list's `tableSettings` through `setTableSettings` only (R32, section
 * 5.5).
 *
 * Give the list's `setRowSelection` to `setRowSelectionRef.current` once
 * `useTable` returns it: "Apply (n)" clears from the selection each row the
 * filter hides.
 */
export function useRebalanceListRows(
  rebalances: RebalanceWithMetrics[] | undefined,
  tableSettings: TableSettings,
  setTableSettings: SetTableSettings,
) {
  const { isPending: isPendingAssignees, data: dataAssignees } =
    useQueryCrmUsers();

  const customFieldsQuery = useQueryHouseholdCustomFields();
  // The `setRowSelection` of `useTable`, which runs after the filter.
  const setRowSelectionRef =
    useRef<Dispatch<SetStateAction<RowSelectionState>>>();

  const allRebalanceRows = useMemo(() => {
    const rows = mapRebalancesToRebalanceRows(rebalances);
    // Enhance household-level rows with assigned user information
    return rows.map((row) => {
      if (row.level === "household") {
        const assignedUser = (dataAssignees?.data ?? []).find(
          (user) => user.id.toString() === row.assignedUserId,
        );
        return {
          ...row,
          assignedUser: assignedUser
            ? {
                id: assignedUser.id.toString(),
                name: assignedUser.name,
              }
            : undefined,
        };
      }
      return row;
    });
  }, [rebalances, dataAssignees?.data]);

  // On "Apply (n)", save the filter and clear from the selection each row that
  // it hides: a hidden rebalance and its account rows (R37).
  const onApplyCustomFieldFilter = useCallback(
    (customFieldFilter: CustomFieldFilter) => {
      setTableSettings((prev) => ({ ...prev, customFieldFilter }));
      const hidden = new Set(
        hiddenRowIds(
          allRebalanceRows,
          customFieldFilter,
          usableDefinitions(customFieldsQuery.data ?? []),
          getRebalanceRowId,
          getRebalanceSubRows,
        ),
      );
      setRowSelectionRef.current?.((selection) =>
        Object.fromEntries(
          Object.entries(selection).filter(([id]) => !hidden.has(id)),
        ),
      );
    },
    [setTableSettings, allRebalanceRows, customFieldsQuery.data],
  );

  const customFields = useCustomFieldsFilter({
    definitionsQuery: customFieldsQuery,
    savedFilter: tableSettings.customFieldFilter,
    onApply: onApplyCustomFieldFilter,
  });

  // The rebalances that match the custom field filter, each with its account
  // rows (R30, R31). With no field set, the same array: a new array resets the
  // table's expanded rows, so the definitions read returning would collapse
  // the account rows of an expanded rebalance.
  const rebalanceRows = useMemo(
    () =>
      customFields.filter.length === 0
        ? allRebalanceRows
        : allRebalanceRows.filter((row) =>
            matchesCustomFieldFilter(
              row,
              customFields.filter,
              customFields.definitions,
            ),
          ),
    [allRebalanceRows, customFields.filter, customFields.definitions],
  );

  return {
    rebalanceRows,
    isPendingAssignees,
    filterBarItems: customFields.filterBarItems,
    setRowSelectionRef,
  };
}
